import React, { useState, useEffect, useCallback, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus, faPen, faTrash, faXmark, faSpinner, faTags, faImages,
  faBolt, faEye, faToggleOn, faToggleOff, faCloudArrowUp,
} from "@fortawesome/free-solid-svg-icons";
import {
  getAdminPromos, createAdminPromo, updateAdminPromo, deleteAdminPromo,
  getPromoProducts, createPromoProduct, updatePromoProduct, deletePromoProduct,
  uploadPromoImage, uploadPromoImagesBatch,
} from "../api/admin";
import "./PromoManagement.css";

const CATEGORY_OPTIONS = [
  { value: "bolsas", label: "Bolsas" },
  { value: "malas", label: "Malas de Viagem" },
  { value: "relogios", label: "Relógios" },
  { value: "acessorios", label: "Acessórios" },
  { value: "joias", label: "Joias" },
  { value: "roupas", label: "Roupas" },
  { value: "calcados", label: "Calçados" },
  { value: "outros", label: "Outros" },
];

const CATEGORY_LABEL = Object.fromEntries(CATEGORY_OPTIONS.map(c => [c.value, c.label]));

const CATEGORY_COLOR = {
  bolsas: "#d4a017",
  malas: "#3b82f6",
  relogios: "#8b5cf6",
  acessorios: "#ec4899",
  joias: "#f59e0b",
  roupas: "#22c55e",
  calcados: "#ef4444",
  outros: "#6b7280",
};

const fmtPrice = (v) => {
  if (!v && v !== 0) return "—";
  return `R$ ${Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 0 })}`;
};

const EMPTY_PROMO = {
  title: "", category: "bolsas", brands: [""],
  pricing_tiers: [{ qty: "", price: "" }],
  badge_text: "", cover_image: "",
  is_active: true, is_vip_only: false,
};

const EMPTY_PRODUCT = { title: "", brand: "", image_url: "", original_price: "" };

const getThumbUrl = (url) => {
  if (!url) return "";
  const dot = url.lastIndexOf(".");
  if (dot === -1) return url;
  return url.slice(0, dot) + "_thumb.jpg?v=2";
};

function isSuperPromoCard(p) {
  const folder = String(p.s3_folder || "").toLowerCase().replace(/_/g, "-");
  const badge = String(p.badge_text || "").toUpperCase();
  const title = String(p.title || "").toUpperCase();
  return (
    folder.includes("super-promo")
    || badge.includes("SUPER PROMO")
    || title.includes("SUPER PROMO")
    || badge.includes("VIP EXCLUSIVA")
  );
}

const PromoManagement = () => {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [kind, setKind] = useState("super");

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_PROMO });

  const [selectedPromo, setSelectedPromo] = useState(null);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({ ...EMPTY_PRODUCT });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragging, setDragging] = useState(false);
  const batchInputRef = useRef(null);

  /* ── Load promos ── */
  const loadPromos = useCallback(async () => {
    setLoading(true);
    try {
      const activeParam = filter === "active" ? true : filter === "inactive" ? false : undefined;
      const res = await getAdminPromos(activeParam);
      setPromos(res.promos || []);
    } catch (e) {
      console.error("Erro ao carregar promoções:", e);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { loadPromos(); }, [loadPromos]);

  const loadProducts = useCallback(async (promoId) => {
    setProductsLoading(true);
    try {
      const res = await getPromoProducts(promoId);
      setProducts(res.products || []);
    } catch (e) {
      console.error("Erro ao carregar produtos:", e);
    } finally {
      setProductsLoading(false);
    }
  }, []);

  /* ── Promo form handlers ── */
  const resetForm = () => { setForm({ ...EMPTY_PROMO }); setEditing(null); setShowForm(false); };

  const openCreate = () => { resetForm(); setShowForm(true); };

  const openEdit = (promo) => {
    setEditing(promo);
    setForm({
      title: promo.title || "",
      category: promo.category || "bolsas",
      brands: promo.brands?.length ? [...promo.brands] : [""],
      pricing_tiers: promo.pricing_tiers?.length
        ? promo.pricing_tiers.map(t => ({ qty: String(t.qty), price: String(t.price) }))
        : [{ qty: "", price: "" }],
      badge_text: promo.badge_text || "",
      cover_image: promo.cover_image || "",
      is_active: promo.is_active !== false,
      is_vip_only: promo.is_vip_only || false,
    });
    setShowForm(true);
  };

  const handleSavePromo = async () => {
    if (!form.title) { alert("Preencha o título da promoção."); return; }
    const tiers = form.pricing_tiers
      .filter(t => t.qty && t.price)
      .map(t => ({ qty: parseInt(t.qty), price: parseFloat(t.price) }));
    if (tiers.length === 0) { alert("Adicione pelo menos uma faixa de preço."); return; }
    const brands = form.brands.filter(b => b.trim());
    const payload = {
      title: form.title, category: form.category, brands,
      pricing_tiers: tiers,
      badge_text: form.badge_text || null,
      cover_image: form.cover_image || null,
      is_active: form.is_active, is_vip_only: form.is_vip_only,
    };
    try {
      if (editing) { await updateAdminPromo(editing.id, payload); }
      else { await createAdminPromo(payload); }
      resetForm();
      loadPromos();
    } catch (e) {
      alert(e?.response?.data?.detail || "Erro ao salvar promoção");
    }
  };

  const handleDeletePromo = async (id) => {
    if (!window.confirm("Tem certeza que deseja remover esta promoção e todos os seus produtos?")) return;
    try { await deleteAdminPromo(id); loadPromos(); }
    catch (e) { alert(e?.response?.data?.detail || "Erro ao remover promoção"); }
  };

  const handleToggleActive = async (promo) => {
    try { await updateAdminPromo(promo.id, { is_active: !promo.is_active }); loadPromos(); }
    catch (e) { alert("Erro ao alterar status"); }
  };

  /* ── Brand helpers ── */
  const handleBrandChange = (idx, value) => {
    const b = [...form.brands]; b[idx] = value;
    setForm(f => ({ ...f, brands: b }));
  };
  const addBrand = () => { if (form.brands.length < 20) setForm(f => ({ ...f, brands: [...f.brands, ""] })); };
  const removeBrand = (idx) => {
    if (form.brands.length <= 1) return;
    setForm(f => ({ ...f, brands: f.brands.filter((_, i) => i !== idx) }));
  };

  /* ── Tier helpers ── */
  const handleTierChange = (idx, field, value) => {
    const t = [...form.pricing_tiers]; t[idx] = { ...t[idx], [field]: value };
    setForm(f => ({ ...f, pricing_tiers: t }));
  };
  const addTier = () => {
    if (form.pricing_tiers.length < 10) setForm(f => ({ ...f, pricing_tiers: [...f.pricing_tiers, { qty: "", price: "" }] }));
  };
  const removeTier = (idx) => {
    if (form.pricing_tiers.length <= 1) return;
    setForm(f => ({ ...f, pricing_tiers: f.pricing_tiers.filter((_, i) => i !== idx) }));
  };

  /* ── Product management ── */
  const openProductsModal = (promo) => { setSelectedPromo(promo); loadProducts(promo.id); };
  const closeProductsModal = () => {
    setSelectedPromo(null); setProducts([]); setShowProductForm(false); setEditingProduct(null);
    loadPromos();
  };
  const resetProductForm = () => { setProductForm({ ...EMPTY_PRODUCT }); setEditingProduct(null); setShowProductForm(false); };
  const openCreateProduct = () => { resetProductForm(); setShowProductForm(true); };
  const openEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      title: product.title || "", brand: product.brand || "",
      image_url: product.image_url || "",
      original_price: product.original_price ? String(product.original_price) : "",
    });
    setShowProductForm(true);
  };

  const handleSaveProduct = async () => {
    if (!productForm.title) { alert("Preencha o título do produto."); return; }
    const payload = {
      title: productForm.title, brand: productForm.brand || null,
      image_url: productForm.image_url || null,
      original_price: productForm.original_price ? parseFloat(productForm.original_price) : null,
      is_available: true,
    };
    try {
      if (editingProduct) { await updatePromoProduct(editingProduct.id, payload); }
      else { await createPromoProduct(selectedPromo.id, payload); }
      resetProductForm();
      loadProducts(selectedPromo.id);
      loadPromos();
    } catch (e) { alert(e?.response?.data?.detail || "Erro ao salvar produto"); }
  };

  const handleToggleAvailable = async (product) => {
    try {
      await updatePromoProduct(product.id, { is_available: !product.is_available });
      loadProducts(selectedPromo.id);
      loadPromos();
    } catch (e) { alert("Erro ao alterar disponibilidade"); }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm("Remover este produto da promoção?")) return;
    try { await deletePromoProduct(id); loadProducts(selectedPromo.id); loadPromos(); }
    catch (e) { alert("Erro ao remover produto"); }
  };

  const handleUpload = async (file, target) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadPromoImage(file);
      if (target === "cover") {
        setForm(f => ({ ...f, cover_image: res.url }));
      } else if (target === "product") {
        setProductForm(f => ({ ...f, image_url: res.url }));
      }
    } catch (e) {
      alert("Erro ao fazer upload da imagem");
    } finally {
      setUploading(false);
    }
  };

  const handleBatchUpload = async (fileList) => {
    const files = Array.from(fileList).filter(f => f.type.startsWith("image/"));
    if (files.length === 0) return;
    setUploading(true);
    setUploadProgress(0);
    try {
      await uploadPromoImagesBatch(selectedPromo.id, files, setUploadProgress);
      loadProducts(selectedPromo.id);
      loadPromos();
    } catch (e) {
      alert("Erro ao fazer upload das imagens");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  /* ══════════════════════════════════════
     RENDER
     ══════════════════════════════════════ */

  const renderLoading = () => (
    <div style={{ textAlign: "center", padding: "60px 0", color: "rgba(0,0,0,0.3)" }}>
      <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: 28, marginBottom: 12 }} />
      <div>Carregando...</div>
    </div>
  );

  const renderPromoForm = () => (
    <div className="promo-form-overlay" onClick={e => e.target === e.currentTarget && resetForm()}>
      <div className="promo-form-modal">
        <div className="promo-form-header">
          <h2>{editing ? "Editar Promoção" : "Nova Promoção"}</h2>
          <button className="promo-form-close" onClick={resetForm}><FontAwesomeIcon icon={faXmark} /></button>
        </div>
        <div className="promo-form-body">
          <div className="promo-field">
            <label>Título da Promoção *</label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder='Ex: BOLSAS CHANEL, HERMES, LORO PIANA...' />
          </div>
          <div className="promo-row-2">
            <div className="promo-field">
              <label>Categoria *</label>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                {CATEGORY_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div className="promo-field">
              <label>Badge / Selo</label>
              <input value={form.badge_text} onChange={e => setForm(f => ({ ...f, badge_text: e.target.value }))} placeholder="Ex: SALE / PROMOÇÃO IMPERDÍVEL" />
            </div>
          </div>

          <div className="promo-field">
            <label><FontAwesomeIcon icon={faTags} style={{ marginRight: 6 }} />Marcas</label>
            {form.brands.map((brand, idx) => (
              <div key={idx} className="promo-brand-row">
                <input value={brand} onChange={e => handleBrandChange(idx, e.target.value)} placeholder="Ex: Chanel" />
                {form.brands.length > 1 && (
                  <button className="promo-row-remove" onClick={() => removeBrand(idx)}><FontAwesomeIcon icon={faTrash} /></button>
                )}
              </div>
            ))}
            <button className="promo-row-add" onClick={addBrand}><FontAwesomeIcon icon={faPlus} /> Adicionar marca</button>
          </div>

          <div className="promo-field">
            <label>Faixas de Preço (Combos) *</label>
            <div className="promo-tiers-header">
              <span>Quantidade</span><span>Preço (R$)</span><span></span>
            </div>
            {form.pricing_tiers.map((tier, idx) => (
              <div key={idx} className="promo-tier-row">
                <input type="number" min="1" value={tier.qty} onChange={e => handleTierChange(idx, "qty", e.target.value)} placeholder="2" />
                <input type="number" min="0" step="100" value={tier.price} onChange={e => handleTierChange(idx, "price", e.target.value)} placeholder="23000" />
                {form.pricing_tiers.length > 1 && (
                  <button className="promo-row-remove" onClick={() => removeTier(idx)}><FontAwesomeIcon icon={faTrash} /></button>
                )}
              </div>
            ))}
            <button className="promo-row-add" onClick={addTier}><FontAwesomeIcon icon={faPlus} /> Adicionar faixa</button>
          </div>

          <div className="promo-field">
            <label><FontAwesomeIcon icon={faImages} style={{ marginRight: 6 }} />Imagem de Capa</label>
            <div className="promo-upload-row">
              <input value={form.cover_image} onChange={e => setForm(f => ({ ...f, cover_image: e.target.value }))} placeholder="URL ou faça upload..." />
              <label className="promo-upload-btn">
                <FontAwesomeIcon icon={uploading ? faSpinner : faCloudArrowUp} spin={uploading} />
                <input type="file" accept="image/*" hidden onChange={e => handleUpload(e.target.files[0], "cover")} />
              </label>
            </div>
            {form.cover_image && <img src={form.cover_image} alt="Capa" className="promo-upload-preview" />}
          </div>

          <div className="promo-toggles">
            <label className="promo-toggle">
              <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
              <span className="promo-toggle-label">Promoção Ativa</span>
            </label>
            <label className="promo-toggle">
              <input type="checkbox" checked={form.is_vip_only} onChange={e => setForm(f => ({ ...f, is_vip_only: e.target.checked }))} />
              <span className="promo-toggle-label"><FontAwesomeIcon icon={faBolt} style={{ marginRight: 4, color: "#d4a017" }} />Apenas VIP</span>
            </label>
          </div>
        </div>
        <div className="promo-form-footer">
          <button className="promo-btn-cancel" onClick={resetForm}>Cancelar</button>
          <button className="promo-btn-save" onClick={handleSavePromo}>{editing ? "Salvar Alterações" : "Criar Promoção"}</button>
        </div>
      </div>
    </div>
  );

  const renderProductsModal = () => (
    <div className="promo-form-overlay" onClick={e => e.target === e.currentTarget && closeProductsModal()}>
      <div className="promo-products-modal">
        <div className="promo-form-header">
          <div>
            <h2>Produtos — {selectedPromo?.title}</h2>
            <p style={{ margin: 0, fontSize: 13, color: "#999" }}>
              {CATEGORY_LABEL[selectedPromo?.category] || selectedPromo?.category}
            </p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="promo-btn-save" style={{ padding: "8px 16px" }} onClick={openCreateProduct}>
              <FontAwesomeIcon icon={faPlus} /> Produto
            </button>
            <button className="promo-form-close" onClick={closeProductsModal}><FontAwesomeIcon icon={faXmark} /></button>
          </div>
        </div>
        <div className="promo-products-body">
          <div
            className={`promo-dropzone ${dragging ? "active" : ""} ${uploading ? "uploading" : ""}`}
            onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDragging(true); }}
            onDragLeave={e => { e.preventDefault(); e.stopPropagation(); setDragging(false); }}
            onDrop={e => { e.preventDefault(); e.stopPropagation(); setDragging(false); handleBatchUpload(e.dataTransfer.files); }}
            onClick={() => !uploading && batchInputRef.current?.click()}
          >
            {uploading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: 24 }} />
                <span className="promo-dropzone-text">Enviando... {uploadProgress}%</span>
                <div className="promo-dropzone-bar"><div className="promo-dropzone-bar-fill" style={{ width: `${uploadProgress}%` }} /></div>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faCloudArrowUp} style={{ fontSize: 28 }} />
                <span className="promo-dropzone-text">Arraste fotos aqui ou clique para selecionar</span>
                <span className="promo-dropzone-hint">JPG, PNG, WEBP — Múltiplas fotos de uma vez</span>
              </>
            )}
            <input ref={batchInputRef} type="file" accept="image/*" multiple hidden onChange={e => { handleBatchUpload(e.target.files); e.target.value = ""; }} />
          </div>

          {selectedPromo?.s3_folder && (
            <div className="promo-s3-folder">
              Pasta S3: <code>{selectedPromo.s3_folder}/</code>
            </div>
          )}

          {productsLoading ? renderLoading() : products.length === 0 ? (
            <div style={{ textAlign: "center", padding: "30px 0", color: "#999", fontSize: 13 }}>
              Nenhum produto ainda. Arraste fotos acima para adicionar em massa.
            </div>
          ) : (
            <div className="promo-products-grid">
              {products.map(p => (
                <div key={p.id} className={`promo-product-card ${!p.is_available ? "sold" : ""}`}>
                  <div className="promo-product-img">
                    {p.image_url ? (
                      <img
                        src={getThumbUrl(p.image_url)}
                        alt={p.title}
                        onError={(e) => { e.target.onerror = null; e.target.src = p.image_url; }}
                        onLoad={(e) => {
                          const isThumb = e.target.src.includes("_thumb");
                          e.target.dataset.thumb = isThumb ? "yes" : "no";
                        }}
                      />
                    ) : (
                      <div className="promo-product-noimg"><FontAwesomeIcon icon={faImages} /></div>
                    )}
                    {!p.is_available && <div className="promo-product-sold-badge">ESGOTADO</div>}
                    {p.image_url && (
                      <div className="promo-thumb-badge" id={`tb-${p.id}`} />
                    )}
                  </div>
                  <div className="promo-product-info">
                    <h4>{p.title}</h4>
                    {p.brand && <span className="promo-product-brand">{p.brand}</span>}
                    {p.original_price && <span className="promo-product-price">{fmtPrice(p.original_price)}</span>}
                  </div>
                  <div className="promo-product-actions">
                    <button className="promo-product-action edit" onClick={() => openEditProduct(p)} title="Editar">
                      <FontAwesomeIcon icon={faPen} />
                    </button>
                    <button
                      className={`promo-product-action ${p.is_available ? "available" : "unavailable"}`}
                      onClick={() => handleToggleAvailable(p)}
                      title={p.is_available ? "Marcar Esgotado" : "Marcar Disponível"}
                    >
                      <FontAwesomeIcon icon={p.is_available ? faToggleOn : faToggleOff} />
                    </button>
                    <button className="promo-product-action delete" onClick={() => handleDeleteProduct(p.id)} title="Remover">
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showProductForm && (
            <div className="promo-product-form">
              <h3>{editingProduct ? "Editar Produto" : "Novo Produto"}</h3>
              <div className="promo-row-2">
                <div className="promo-field">
                  <label>Nome do Produto *</label>
                  <input value={productForm.title} onChange={e => setProductForm(f => ({ ...f, title: e.target.value }))} placeholder="Ex: Chanel Classic Flap Medium" />
                </div>
                <div className="promo-field">
                  <label>Marca</label>
                  <input value={productForm.brand} onChange={e => setProductForm(f => ({ ...f, brand: e.target.value }))} placeholder="Ex: Chanel" />
                </div>
              </div>
              <div className="promo-row-2">
                <div className="promo-field">
                  <label>Imagem do Produto</label>
                  <div className="promo-upload-row">
                    <input value={productForm.image_url} onChange={e => setProductForm(f => ({ ...f, image_url: e.target.value }))} placeholder="URL ou faça upload..." />
                    <label className="promo-upload-btn">
                      <FontAwesomeIcon icon={uploading ? faSpinner : faCloudArrowUp} spin={uploading} />
                      <input type="file" accept="image/*" hidden onChange={e => handleUpload(e.target.files[0], "product")} />
                    </label>
                  </div>
                  {productForm.image_url && <img src={productForm.image_url} alt="Preview" className="promo-upload-preview" />}
                </div>
                <div className="promo-field">
                  <label>Preço Avulso (R$)</label>
                  <input type="number" step="100" value={productForm.original_price} onChange={e => setProductForm(f => ({ ...f, original_price: e.target.value }))} placeholder="18000" />
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                <button className="promo-btn-cancel" onClick={resetProductForm}>Cancelar</button>
                <button className="promo-btn-save" onClick={handleSaveProduct}>{editingProduct ? "Salvar" : "Adicionar"}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="promo-toolbar">
        <div className="promo-filters">
          {[{ key: "super", label: "Super Promo" }, { key: "other", label: "Outras" }, { key: "all", label: "Todas" }].map(f => (
            <button key={f.key} className={`promo-filter-btn ${kind === f.key ? "active" : ""}`} onClick={() => setKind(f.key)}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="promo-filters">
          {[{ key: "all", label: "Todas" }, { key: "active", label: "Ativas" }, { key: "inactive", label: "Inativas" }].map(f => (
            <button key={f.key} className={`promo-filter-btn ${filter === f.key ? "active" : ""}`} onClick={() => setFilter(f.key)}>
              {f.label}
            </button>
          ))}
        </div>
        <button className="promo-btn-create" onClick={openCreate}>
          <FontAwesomeIcon icon={faPlus} /> Nova Promoção
        </button>
      </div>

      {loading ? renderLoading() : promos.length === 0 ? (
        <div className="promo-empty">
          <FontAwesomeIcon icon={faTags} style={{ fontSize: 36, marginBottom: 12, opacity: 0.3 }} />
          <div>Nenhuma promoção encontrada.</div>
          <button className="promo-btn-create" style={{ marginTop: 16 }} onClick={openCreate}>
            <FontAwesomeIcon icon={faPlus} /> Criar Primeira Promoção
          </button>
        </div>
      ) : (
        <div className="promo-list">
          {promos.filter((promo) => kind === "all" || (kind === "super") === isSuperPromoCard(promo)).map(promo => (
            <div key={promo.id} className={`promo-card ${promo.is_active ? "active" : "inactive"}`}>
              <div className="promo-card-left">
                {(promo.preview_images?.length > 0) ? (
                  <div className="promo-card-thumbstrip">
                    {promo.preview_images.slice(0, 4).map((url, i) => (
                      <img key={i} src={getThumbUrl(url)} alt="" className="promo-card-thumb-img" onError={(e) => { e.target.onerror = null; e.target.src = url; }} />
                    ))}
                    {promo.preview_images.length > 4 && (
                      <div className="promo-card-thumb-more">+{promo.preview_images.length - 4}</div>
                    )}
                  </div>
                ) : promo.cover_image ? (
                  <img src={getThumbUrl(promo.cover_image)} alt="" className="promo-card-cover" onError={(e) => { e.target.onerror = null; e.target.src = promo.cover_image; }} />
                ) : (
                  <div className="promo-card-cover-placeholder" style={{ borderColor: (CATEGORY_COLOR[promo.category] || "#999") + "33", color: CATEGORY_COLOR[promo.category] || "#999" }}>
                    <FontAwesomeIcon icon={faTags} />
                  </div>
                )}
              </div>
              <div className="promo-card-body">
                <div className="promo-card-top-row">
                  <div className="promo-card-badges">
                    <span className="promo-badge-category" style={{ background: (CATEGORY_COLOR[promo.category] || "#999") + "18", color: CATEGORY_COLOR[promo.category] || "#999" }}>
                      {CATEGORY_LABEL[promo.category] || promo.category}
                    </span>
                    {isSuperPromoCard(promo) && <span className="promo-badge-vip">SUPER</span>}
                    {promo.is_vip_only && <span className="promo-badge-vip"><FontAwesomeIcon icon={faBolt} /> VIP</span>}
                    <span className={`promo-badge-status ${promo.is_active ? "on" : "off"}`}>
                      {promo.is_active ? "Ativa" : "Inativa"}
                    </span>
                    {promo.badge_text && <span className="promo-badge-custom">{promo.badge_text}</span>}
                  </div>
                </div>
                <h3 className="promo-card-title">{promo.title}</h3>
                {promo.brands?.length > 0 && (
                  <div className="promo-card-brands">
                    {promo.brands.map((b, i) => <span key={i} className="promo-brand-pill">{b}</span>)}
                  </div>
                )}
                {promo.pricing_tiers?.length > 0 && (
                  <div className="promo-card-tiers">
                    {promo.pricing_tiers.map((t, i) => (
                      <div key={i} className="promo-tier-pill">
                        <span className="promo-tier-qty">{t.qty} {t.qty === 1 ? "peça" : "peças"}</span>
                        <span className="promo-tier-price">{fmtPrice(t.price)}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="promo-card-footer">
                  <span className="promo-card-product-count">
                    <FontAwesomeIcon icon={faImages} style={{ marginRight: 5 }} />
                    {promo.product_count || 0} produto{(promo.product_count || 0) !== 1 ? "s" : ""}
                    {(promo.product_count || 0) > 0 && (
                      <span className="promo-card-available"> ({promo.available_count || 0} disponíve{(promo.available_count || 0) !== 1 ? "is" : "l"})</span>
                    )}
                  </span>
                  <button className="promo-btn-products" onClick={() => openProductsModal(promo)}>
                    <FontAwesomeIcon icon={faEye} /> Produtos
                  </button>
                </div>
              </div>
              <div className="promo-card-actions">
                <button className="promo-action-btn edit" onClick={() => openEdit(promo)} title="Editar"><FontAwesomeIcon icon={faPen} /></button>
                <button className="promo-action-btn toggle" onClick={() => handleToggleActive(promo)} title={promo.is_active ? "Desativar" : "Ativar"}>
                  <FontAwesomeIcon icon={promo.is_active ? faToggleOn : faToggleOff} style={{ color: promo.is_active ? "#22c55e" : "#999" }} />
                </button>
                <button className="promo-action-btn delete" onClick={() => handleDeletePromo(promo.id)} title="Remover"><FontAwesomeIcon icon={faTrash} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && renderPromoForm()}
      {selectedPromo && renderProductsModal()}
    </>
  );
};

export default PromoManagement;
